import fetchData from "../API/Fetch";

/**
 * Fetches comprehensive residence usage data from the backend
 *
 * @param {string|number} residenceId - The ID of the residence
 * @param {object} options - Options for the fetch request
 * @param {Date|string} options.startDate - Start date for the usage data
 * @param {Date|string} options.endDate - End date for the usage data
 * @param {string} options.groupedBy - How to group the data ("hourly" or "daily")
 * @returns {Promise<object>} - The residence usage data
 */
const fetchResidenceUsage = async (residenceId, options = {}) => {
  try {
    const { startDate, endDate, groupedBy = "hourly" } = options;

    // Build query parameters
    const params = new URLSearchParams();
    if (startDate)
      params.append(
        "start_date",
        startDate.toISOString ? startDate.toISOString() : startDate
      );
    if (endDate)
      params.append(
        "end_date",
        endDate.toISOString ? endDate.toISOString() : endDate
      );
    if (groupedBy) params.append("grouped_by", groupedBy);

    // Build the URL with query parameters
    const url = `/residence-usage/${residenceId}${
      params.toString() ? "?" + params.toString() : ""
    }`;

    // Fetch the data
    const response = await fetchData(url);

    // Handle case where response might not have ok property (fetchData might handle errors differently)
    if (response && response.error) {
      throw new Error(`Error fetching residence usage: ${response.error}`);
    }

    // Handle case where response has ok property (standard fetch response)
    if (response && response.ok === false) {
      const status = response.status || "unknown";
      const statusText = response.statusText || "unknown error";
      throw new Error(
        `Error fetching residence usage: ${status} ${statusText}`
      );
    }

    // If response is not JSON or doesn't have data, handle gracefully
    if (!response || typeof response !== "object") {
      return {}; // Return empty object instead of throwing
    }

    // If response already contains data directly (not requiring json parsing)
    if (response.data) {
      return response.data;
    }

    // Standard case - parse JSON from response
    try {
      const data = response.json ? await response.json() : response;
      return data;
    } catch (jsonError) {
      return {}; // Return empty object instead of throwing
    }
  } catch (error) {
    // Return an object with error information instead of throwing
    // This allows the component to handle the error gracefully
    return {
      error: true,
      message: error.message || "Failed to fetch residence usage data",
      details: error.toString(),
    };
  }
};

export default fetchResidenceUsage;
